import {
  faDrawCircle,
  faFolders,
  faGear,
  faHandsHoldingHeart,
  faHome,
  faMicroscope,
  faQuestionCircle,
  faRectanglePro,
  faRocket,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useRouteMatch } from 'react-router-dom';

import { EOnboardingTaskState, EPackageTiers } from '@feathr/blackbox';
import {
  AvatarV2 as Avatar,
  Badge,
  ListItem,
  Logo,
  Nav,
  NavItem,
  NavItemHeader,
  NavMenu,
  NavMenuProvider,
  Pill,
  Popover,
  Skeleton,
} from '@feathr/components';
import { TrackBlocked } from '@feathr/extender/components';
import { useAccountSwitcher } from '@feathr/extender/components/AccountSwitcher';
import AccountSwitcher from '@feathr/extender/components/AccountSwitcher';
import NotificationsPopover from '@feathr/extender/components/NotificationsPopover';
import {
  EOnboardingProgressiveFeatures,
  SessionCacheProvider,
  useBlackboxVersion,
  useFeatureLimits,
  useLicense,
  useOnboardingStatus,
} from '@feathr/extender/hooks';
import { useAccount, useFlags, useLocalUrl, useUser } from '@feathr/extender/state';

import DataNav from '../DataNav';
import EmailUsageIndicator from '../EmailUsageIndicator';
import EventNav from '../EventNav';
import HelpCard from '../HelpCard/HelpCard';
import HelpNav from '../HelpNav';
import HomeNav from '../HomeNav';
import SettingsNav from '../SettingsNav';
import UserNav from '../UserNav';

import styles from './PrimaryNavV1.css';

interface IProps {
  className?: string;
  secondaryNav?: ReactNode;
}

function PrimaryNavV1({ className }: Readonly<IProps>): JSX.Element | null {
  // Get routing, account, and user info
  const localUrl = useLocalUrl();
  const { blackboxVersion, isOnProduction } = useBlackboxVersion();
  const match = useRouteMatch<{ accountId: string; primary: string }>({
    path: '/:accountId/:primary',
  });
  const account = useAccount();
  const user = useUser();
  const hasProResources = useLicense('proResourcesPageVisible');
  const {
    isLoading: isProjectsLoading,
    task: projectsTask,
    message: projectsMessage,
  } = useOnboardingStatus(EOnboardingProgressiveFeatures.Projects);
  const {
    isLoading: isHomeLoading,
    task: homeTask,
    message: homeMessage,
  } = useOnboardingStatus(EOnboardingProgressiveFeatures.Home);
  const { isLoading: isCommunityLoading } = useOnboardingStatus(
    EOnboardingProgressiveFeatures.Community,
  );
  const { isMilestonesProgressLoading, milestonesProgress } = useOnboardingStatus();
  const { canSwitchAccounts, isLoading } = useAccountSwitcher({ accountId: account.id });
  const { t } = useTranslation();
  const { showOnboarding, enforceOnboarding, showFundraise } = useFlags();
  const { isEmailLimitsEnabled } = useFeatureLimits();
  const picture = (!!user && user.get('picture')) || '';
  const showHelpCard =
    account.isPlatformLite && user.getSetting('show_feathr_lite_help_card', false);
  const isHomeLocked =
    showOnboarding &&
    enforceOnboarding &&
    homeTask?.get('state') !== EOnboardingTaskState.Completed;
  const isProjectsLocked =
    showOnboarding &&
    enforceOnboarding &&
    projectsTask?.get('state') !== EOnboardingTaskState.Completed;
  const homeTooltip = showOnboarding && enforceOnboarding && isHomeLocked ? homeMessage : undefined;

  const projectsTooltip =
    showOnboarding && enforceOnboarding && isProjectsLocked ? projectsMessage : undefined;

  const userName = user.get('name') ?? user.get('email') ?? user.name;

  // Ref for the scrollable nav element
  const scrollableNavRef = useRef<HTMLDivElement>(null);
  const [showShadow, setShowShadow] = useState(false);

  // Check if scrollable content is taller than its container
  const checkOverflow = useCallback(() => {
    if (!scrollableNavRef.current) {
      return;
    }
    const node = scrollableNavRef.current;
    const hasOverflow = node.scrollHeight > node.clientHeight;
    setShowShadow(hasOverflow);
  }, []);

  // Setup overflow detection
  useEffect(() => {
    // Initial check
    checkOverflow();

    // Check periodically for changes
    const intervalId = setInterval(() => {
      checkOverflow();
    }, 100);

    return () => {
      clearInterval(intervalId);
    };
  }, [checkOverflow]);

  if (!match) {
    return null;
  }

  let primaryNavComponent: JSX.Element;
  if (isLoading || isProjectsLoading || isHomeLoading || isCommunityLoading) {
    primaryNavComponent = <Skeleton className={styles.skeleton} />;
  } else if (canSwitchAccounts) {
    primaryNavComponent = (
      <ListItem className={styles.accountSwitcher}>
        <AccountSwitcher />
      </ListItem>
    );
  } else if (account.isPlatformLite) {
    primaryNavComponent = (
      <>
        <Logo className={styles.logo} />
        <Pill theme={'sky'}>Lite</Pill>
      </>
    );
  } else {
    primaryNavComponent = <Logo className={styles.logo} />;
  }

  return (
    <NavMenuProvider>
      <Nav
        className={classNames(styles.root, className)}
        id={'primary-nav'}
        label={'Primary navigation'}
        type={'vertical'}
      >
        <span className={styles.logoContainer}>{primaryNavComponent}</span>

        <div className={styles.scrollableNav} data-name={'scrollable-nav'} ref={scrollableNavRef}>
          {(account.hasActivePackage || user?.isSudoer) && (
            <>
              {showOnboarding && (
                <NavItem
                  data-command={'true'}
                  data-command-description={t('Get started with onboarding')}
                  data-command-keywords={'quickstart,onboarding,getting started'}
                  data-command-label={t('Quick Start')}
                  icon={faRocket}
                  suffix={
                    <Link to={localUrl('/quickstart')}>
                      <Badge
                        size={'small'}
                        theme={'blue'}
                        value={
                          isMilestonesProgressLoading ? (
                            <Skeleton height={20} width={40} />
                          ) : (
                            `${milestonesProgress.completed} / ${milestonesProgress.total}`
                          )
                        }
                      />
                    </Link>
                  }
                  to={localUrl('/quickstart')}
                >
                  {t('Quick Start')}
                </NavItem>
              )}
              <TrackBlocked
                featureName={'home'}
                locked={isHomeLocked}
                requiredPackage={isHomeLocked ? EPackageTiers.Starter : undefined}
              >
                <NavMenu
                  data-command={'true'}
                  data-command-description={t('Home dashboard and marketing')}
                  data-command-keywords={'home,dashboard,marketing,overview'}
                  data-command-label={t('Home')}
                  icon={faHome}
                  // Home menu needs to expand only for submenu items and not just look at path
                  isActive={['accounts', 'dashboard', 'marketing'].includes(match.params.primary)}
                  menu={<HomeNav />}
                  rel={'home'}
                  to={localUrl('')}
                  tooltip={homeTooltip}
                >
                  {t('Home')}
                </NavMenu>
              </TrackBlocked>

              {showFundraise && (
                <TrackBlocked featureName={'fundraise'}>
                  <NavItem icon={faHandsHoldingHeart} to={localUrl('/fundraise')}>
                    {t('Fundraising')}
                  </NavItem>
                </TrackBlocked>
              )}

              <TrackBlocked
                featureName={'projects'}
                locked={isProjectsLocked}
                requiredPackage={isProjectsLocked ? EPackageTiers.Starter : undefined}
              >
                <NavMenu
                  data-command={'true'}
                  data-command-description={t('Manage projects and campaigns')}
                  data-command-keywords={'projects,campaigns,events'}
                  data-command-label={t('Projects')}
                  icon={faFolders}
                  menu={<EventNav />}
                  to={localUrl('/projects')}
                  tooltip={projectsTooltip}
                >
                  {t('Projects')}
                </NavMenu>
              </TrackBlocked>

              <NavMenu
                data-command={'true'}
                data-command-description={t('Community data and people management')}
                data-command-keywords={'community,data,people,segments,groups'}
                data-command-label={t('Community')}
                icon={faDrawCircle}
                menu={<DataNav />}
                to={localUrl('/data')}
              >
                {t('Community')}
              </NavMenu>
            </>
          )}
          <NavMenu
            data-command={'true'}
            data-command-description={t('Manage account settings')}
            data-command-keywords={'settings,account,preferences'}
            data-command-label={t('Account Settings')}
            icon={faGear}
            menu={<SettingsNav />}
            to={localUrl('/settings')}
          >
            {t('Account Settings')}
          </NavMenu>
        </div>
        <div
          className={styles.fixedBottomNav}
          data-name={'primary-nav-bottom'}
          style={
            {
              ...(showShadow
                ? { '--fixed-bottom-nav-shadow-opacity': 0.12 }
                : { '--fixed-bottom-nav-shadow-opacity': 0 }),
            } as React.CSSProperties
          }
        >
          {!!blackboxVersion && !isOnProduction && account.isTestAccount && (
            <NavItem
              data-command={'true'}
              data-command-description={t('Development and QA tools')}
              data-command-keywords={'development,qa,testing,debug'}
              data-command-label={t('Development & QA')}
              icon={faMicroscope}
              to={localUrl('/development-qa')}
            >
              {t('Development & QA')}
            </NavItem>
          )}

          <NavItem
            className={styles.help}
            footer={
              <>
                {showHelpCard && <HelpCard />}
                {isEmailLimitsEnabled && (
                  <SessionCacheProvider>
                    <EmailUsageIndicator />
                  </SessionCacheProvider>
                )}
                <Popover
                  align={'end'}
                  className={styles.popper}
                  position={'right'}
                  toggle={'onClick'}
                >
                  <NavItemHeader prefix={<FontAwesomeIcon icon={faQuestionCircle} />}>
                    {t('Help')}
                  </NavItemHeader>
                  <HelpNav />
                </Popover>
              </>
            }
          />

          {hasProResources && (
            <NavItem
              data-command={'true'}
              data-command-description={t('Access professional resources')}
              data-command-keywords={'pro,resources,professional'}
              data-command-label={t('Pro Resources')}
              icon={<FontAwesomeIcon icon={faRectanglePro} />}
              to={localUrl('/settings/pro-resources')}
            >
              {t('Resources')}
            </NavItem>
          )}
          <NavItem className={styles.notifications} footer={<NotificationsPopover />} />
          <NavItem
            className={styles.avatar}
            footer={
              <Popover
                align={'end'}
                className={styles.popper}
                position={'right'}
                toggle={'onClick'}
              >
                <NavItemHeader prefix={<Avatar image={picture} name={userName} size={'xs'} />}>
                  {/* If name is not set, try to use email, else use "Unnamed User */}
                  {userName}
                </NavItemHeader>
                <UserNav />
              </Popover>
            }
          />
        </div>
      </Nav>
    </NavMenuProvider>
  );
}

export default observer(PrimaryNavV1);
